/**
 * ChatMessages — scrollable message list with sending indicator.
 * Extracted from PersistentChatPanel for reuse. Sprint T1c.
 *
 * Props:
 *   messages   — array of { id, role, content, cards, intent, isError, quick_replies }
 *   sending    — boolean
 *   onSend     — (text: string) => void  (for quick replies)
 *   compact    — boolean, passed to MessageBubble
 *   emptyLabel — string, override for placeholder text
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MessageBubble from './MessageBubble';

export default function ChatMessages({ messages = [], sending = false, onSend, compact = false, emptyLabel }) {
  const { t } = useTranslation();
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  return (
    <div className="flex-1 overflow-y-auto px-3 py-3 min-h-0" data-testid="chat-messages">
      {messages.length === 0 && (
        <div className="flex flex-col items-center justify-center h-full text-center px-3">
          <div className="w-10 h-10 bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)]
                          rounded-xl flex items-center justify-center mb-2">
            <span className="text-[var(--aif-color-primary)] font-bold text-sm">A</span>
          </div>
          <p className="text-xs text-[var(--aif-text-muted)] max-w-[200px] leading-relaxed">
            {emptyLabel || t('chat.welcomeMessage', { defaultValue: 'Frag mich alles über deinen Betrieb.' })}
          </p>
        </div>
      )}

      {messages.map((msg, i) => (
        <MessageBubble
          key={msg.id || i}
          message={msg}
          compact={compact}
          onSendMessage={onSend}
        />
      ))}

      {sending && (
        <div className="flex justify-start mb-2">
          <div className="bg-[var(--aif-bg-elevated)] rounded-2xl rounded-bl-md px-3 py-2">
            <div className="flex gap-1">
              <span className="w-1.5 h-1.5 bg-[var(--aif-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 bg-[var(--aif-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 bg-[var(--aif-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      )}

      <div ref={endRef} />
    </div>
  );
}
